import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Archive, Eye } from "lucide-react";
import { toast } from "sonner";

const MANAGE_ROLES = ["service_desk_manager", "l3_technician", "super_admin", "msp_owner"];

export default function ArticleDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  // Best-effort name lookup. KB is staff-only (client_scope_guard refuses a
  // portal role before any handler here runs), so any viewer of this page can
  // also read /users -- but a name is a courtesy, not a requirement, so a
  // failed lookup degrades to the raw id rather than breaking the page.
  const [userNames, setUserNames] = useState({});

  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/users").then((r) => setUserNames(
      Object.fromEntries(asArray(r.data).map((u) => [u.id, u.name])),
    )).catch(() => setUserNames({}));
  }, []);
  const mayManage = !!me && MANAGE_ROLES.includes(me.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get(`/kb/${id}`);
      setArticle(r.data && typeof r.data === "object" ? r.data : null);
    } catch {
      setError(true);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function save(draft) {
    try {
      await api.patch(`/kb/${draft.id}`, {
        title: draft.title, body_md: draft.body_md, category: draft.category, tags: draft.tags,
      });
      setEditing(false);
      await load();
      toast.success("Article updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function archive() {
    // Archiving is irreversible in this pass (no unarchive route yet -- see
    // the follow-up noted in the final whole-branch review), unlike the
    // less consequential canned-response delete, which already confirms
    // first (ServiceDesk.jsx). A single click must not be enough here.
    if (!window.confirm(`Archive "${article.title}"? This cannot be undone from this screen.`)) return;
    setArchiving(true);
    try {
      await api.post(`/kb/${id}/archive`);
      await load();
      toast.success("Article archived");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setArchiving(false); }
  }

  if (loading) return <div className="text-zinc-500 p-6">Loading…</div>;
  if (error || !article) {
    return (
      <div className="space-y-4">
        <Link to="/knowledge" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 w-fit">
          <ArrowLeft size={14} /> Back to Knowledge Base
        </Link>
        <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-6 text-zinc-500 text-sm">
          Could not load this article.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link to="/knowledge" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 w-fit">
        <ArrowLeft size={14} /> Back to Knowledge Base
      </Link>

      {article.archived_at && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-md px-4 py-2 text-sm">
          This article was archived on {String(article.archived_at).slice(0, 10)}.
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {article.category && (
              <span className="text-[10px] uppercase tracking-widest font-mono text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5">
                {article.category}
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">{article.title}</h1>
          <div className="flex items-center gap-4 text-[11px] text-zinc-500 font-mono mt-2">
            <span>By {userNames[article.author_id] || article.author_id || "—"}</span>
            <span className="flex items-center gap-1"><Eye size={12} /> {article.view_count ?? 0} views</span>
            <span>Updated {(article.updated_at || article.created_at || "").slice(0, 10) || "—"}</span>
          </div>
          {(article.tags || []).length > 0 && (
            <div className="flex gap-1 flex-wrap mt-2">
              {article.tags.map((t) => (
                <span key={t} className="text-[11px] text-zinc-500 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        {mayManage && (
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setEditing(true)} data-testid="article-edit-btn"
              className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 flex items-center gap-1.5 hover:bg-zinc-900/60">
              <Pencil size={14} /> Edit
            </button>
            {!article.archived_at && (
              <button onClick={archive} disabled={archiving} data-testid="article-archive-btn"
                className="border border-red-900/60 rounded-md px-3 py-1.5 text-sm text-red-400 flex items-center gap-1.5 hover:bg-red-500/10 disabled:opacity-50">
                <Archive size={14} /> {archiving ? "Archiving…" : "Archive"}
              </button>
            )}
          </div>
        )}
      </div>

      <div
        className="prose prose-invert max-w-none bg-zinc-950 border border-zinc-900 rounded-md p-6"
        data-testid="article-body-rendered"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(article.body_md) }}
      />

      {editing && (
        <ArticleEditDialog article={article} onCancel={() => setEditing(false)} onSave={save} />
      )}
    </div>
  );
}

function ArticleEditDialog({ article, onCancel, onSave }) {
  const [draft, setDraft] = useState({
    id: article.id, title: article.title || "", body_md: article.body_md || "",
    category: article.category || "General", tags: article.tags || [],
  });
  const [tagsText, setTagsText] = useState((article.tags || []).join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.title.trim()) return toast.error("A title is required.");
    setBusy(true);
    try {
      await onSave({
        ...draft,
        title: draft.title.trim(),
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent
        data-testid="article-editor-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader><DialogTitle className="font-display">Edit article</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-zinc-400">Title</span>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              data-testid="article-title" autoFocus
              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400">Category</span>
            <input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              data-testid="article-category"
              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
          </label>
        </div>

        <label className="block">
          <span className="text-xs text-zinc-400">Tags (comma-separated)</span>
          <input value={tagsText} onChange={(e) => setTagsText(e.target.value)}
            data-testid="article-tags"
            className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-zinc-400">Body (Markdown)</span>
            <textarea
              data-testid="article-body"
              className="w-full h-96 mt-1 font-mono text-sm bg-zinc-900 border border-zinc-800 rounded p-3 focus:outline-none focus:border-blue-500/60"
              value={draft.body_md}
              onChange={(e) => setDraft({ ...draft, body_md: e.target.value })}
            />
          </div>
          <div>
            <span className="text-xs text-zinc-400">Preview</span>
            {/* renderMarkdown is the ONLY sanitised path; never call marked directly here */}
            <div
              data-testid="article-preview"
              className="h-96 mt-1 overflow-auto prose prose-invert max-w-none bg-zinc-900 border border-zinc-800 rounded p-3 text-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(draft.body_md) }}
            />
          </div>
        </div>

        <DialogFooter>
          <button onClick={onCancel} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="article-save"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
            {busy ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
